/**
 * تحقق مدخلات نموذج «قيّم أرض».
 *
 * هذا تحقق شكلي من المدخلات (Input Validation) وليس منطق تقييم — لا يحسب
 * قيمة ولا مؤشراً، ويعيد رسائل عربية جاهزة للعرض.
 */

export interface EvaluateFormValues {
  district: string;
  city: string;
  area: string;
  price: string;
}

export interface EvaluateFormErrors {
  district?: string;
  area?: string;
  price?: string;
}

export const MIN_AREA_SQM = 50;
export const MAX_AREA_SQM = 500_000;
export const MIN_PRICE = 10_000;

/** يحوّل نصاً رقمياً (قد يحتوي فواصل) إلى رقم، أو NaN. */
export function parseNumeric(raw: string): number {
  const cleaned = raw.replace(/[,\s_]/g, '');
  if (!cleaned) return NaN;
  return Number(cleaned);
}

export function validateEvaluateForm(v: EvaluateFormValues): EvaluateFormErrors {
  const errors: EvaluateFormErrors = {};

  if (!v.district.trim()) {
    errors.district = 'اختر الحي.';
  }

  const area = parseNumeric(v.area);
  if (!Number.isFinite(area)) {
    errors.area = 'أدخل المساحة بالمتر المربع.';
  } else if (area < MIN_AREA_SQM) {
    errors.area = `المساحة أقل من الحد المقبول (${MIN_AREA_SQM} م²).`;
  } else if (area > MAX_AREA_SQM) {
    errors.area = 'المساحة أكبر من الحد المقبول.';
  }

  const price = parseNumeric(v.price);
  if (!Number.isFinite(price)) {
    errors.price = 'أدخل السعر المطلوب بالريال.';
  } else if (price < MIN_PRICE) {
    errors.price = 'السعر المطلوب أقل من الحد المقبول.';
  }

  return errors;
}

export function isValid(errors: EvaluateFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

/** سعر المتر المطلوب — عرض فوري داخل النموذج فقط (قسمة، ليست تقييماً). */
export function askingPerSqmHint(area: string, price: string): number | null {
  const a = parseNumeric(area);
  const p = parseNumeric(price);
  if (!Number.isFinite(a) || !Number.isFinite(p) || a <= 0) return null;
  return Math.round(p / a);
}

// ---------------------------------------------------------------------------
// الموقع الاختياري
// ---------------------------------------------------------------------------

/**
 * حدود السعودية — فحص معقولية للمدخل فقط.
 * التحقق الحقيقي والتحويل الجغرافي يبقيان على الخادم.
 */
export const SAUDI_INPUT_BOUNDS = {
  minLat: 16.0,
  maxLat: 32.5,
  minLng: 34.0,
  maxLng: 56.0,
} as const;

export interface ParsedLocation {
  latitude: number;
  longitude: number;
}

export type LocationParseError = 'empty' | 'unrecognized' | 'out-of-bounds';

export type LocationParseResult =
  | { ok: true; value: ParsedLocation }
  | { ok: false; error: LocationParseError };

/**
 * يقبل الصيغ التي يستخدمها الناس فعلاً:
 *   «24.6703, 46.5375»
 *   رابط خرائط جوجل يحتوي @lat,lng أو q=lat,lng أو !3dlat!4dlng
 *
 * لا يخمّن ولا يصحّح: ما لا يُفهم يُرفض برسالة واضحة.
 */
export function parseLocationInput(raw: string): LocationParseResult {
  const text = raw.trim();
  if (!text) return { ok: false, error: 'empty' };

  const candidates: [number, number][] = [];
  const pair = /(-?\d{1,3}\.\d+)\s*[,\s]\s*(-?\d{1,3}\.\d+)/.exec(text);
  if (pair) candidates.push([Number(pair[1]), Number(pair[2])]);

  const at = /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/.exec(text);
  if (at) candidates.push([Number(at[1]), Number(at[2])]);

  const dm = /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/.exec(text);
  if (dm) candidates.push([Number(dm[1]), Number(dm[2])]);

  if (candidates.length === 0) return { ok: false, error: 'unrecognized' };

  const [latitude, longitude] = candidates[0]!;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { ok: false, error: 'unrecognized' };
  }
  if (
    latitude < SAUDI_INPUT_BOUNDS.minLat ||
    latitude > SAUDI_INPUT_BOUNDS.maxLat ||
    longitude < SAUDI_INPUT_BOUNDS.minLng ||
    longitude > SAUDI_INPUT_BOUNDS.maxLng
  ) {
    return { ok: false, error: 'out-of-bounds' };
  }
  return { ok: true, value: { latitude, longitude } };
}

export const LOCATION_ERROR_MESSAGE: Record<LocationParseError, string> = {
  empty: '',
  unrecognized: 'لم نتعرّف على الموقع. الصق إحداثيات مثل 24.6703, 46.5375 أو رابط خرائط جوجل.',
  'out-of-bounds': 'الموقع خارج حدود المملكة.',
};

// ---------------------------------------------------------------------------
// الإدخال السريع — مسار المستخدم المتكرر
// ---------------------------------------------------------------------------

export interface QuickEntryValue {
  district: string;
  /** نص كما كتبه المستخدم — يمرّ على parseNumeric مثل أي حقل. */
  area: string;
  price: string;
}

export type QuickEntryError = 'empty' | 'missing-numbers' | 'missing-district';

export type QuickEntryResult =
  | { ok: true; value: QuickEntryValue }
  | { ok: false; error: QuickEntryError };

/** يحوّل الأرقام العربية والفارسية إلى لاتينية — عرض فقط، لا تغيير في القيمة. */
function toLatinDigits(text: string): string {
  return text.replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (d) => {
    const code = d.codePointAt(0)!;
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
}

/** وحدات يكتبها الناس بجانب الأرقام — تُزال من اسم الحي لا من القيمة. */
const UNIT_NOISE = /(م²|م2|متر مربع|متر|ر\.?\s?س|ريال|sar|sqm|m2)/gi;

/**
 * يفكّ سطراً واحداً مثل «النرجس 500 2,400,000» إلى ثلاثة حقول.
 *
 * الترتيب المعلن: الحي ثم المساحة ثم السعر. لا يخمّن الترتيب ولا يعيد
 * ترتيب الأرقام حسب حجمها؛ الواجهة تعرض ما فُهم قبل الحساب فيراه المستخدم.
 */
export function parseQuickEntry(raw: string): QuickEntryResult {
  const text = toLatinDigits(raw).trim();
  if (!text) return { ok: false, error: 'empty' };

  const numbers = text.match(/\d[\d,._]*/g) ?? [];
  if (numbers.length < 2) return { ok: false, error: 'missing-numbers' };

  const district = text
    .replace(/\d[\d,._]*/g, ' ')
    .replace(UNIT_NOISE, ' ')
    .replace(/[·,،|/\\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!district) return { ok: false, error: 'missing-district' };

  return { ok: true, value: { district, area: numbers[0]!, price: numbers[1]! } };
}

export const QUICK_ENTRY_ERROR_MESSAGE: Record<QuickEntryError, string> = {
  empty: '',
  'missing-numbers': 'اكتب الحي ثم المساحة ثم السعر — مثال: النرجس 500 2,400,000',
  'missing-district': 'ينقص اسم الحي قبل الأرقام.',
};
